import { createApi } from '@reduxjs/toolkit/query/react';
import { Comment } from './models';
import { baseApi } from '@centerpiece/components';

export const comment = baseApi
  .enhanceEndpoints({ addTagTypes: ['Comments', 'Comment'] })
  .injectEndpoints({
    endpoints: builder => ({
      getComments: builder.query<Comment[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'comment',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: 'Comment' as const, id })),
                'Comments',
              ]
            : ['Comments'],
      }),
      getComment: builder.query<Comment, string>({
        query: id => ({
          url: `comment/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [{ type: 'Comment', id: arg }],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createComment: builder.mutation({
        query: request => ({
          url: 'comment',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Comments'],
      }),
      patchComment: builder.mutation<Comment, Comment>({
        query: request => ({
          url: `comment/${request.id}`,
          method: 'PATCH',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Comment', id: arg.id },
        ],
      }),
      updateComment: builder.mutation<Comment, Comment>({
        query: request => ({
          url: `comment/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Comment', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            comment.util.updateQueryData('getComment', `${id}`, draft => {
              Object.assign(draft, patch);
            })
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(comment.util.invalidateTags(['Comments']))
             */
            dispatch(comment.util.invalidateTags(['Comments']));
          }
        },
      }),
      deleteComment: builder.mutation<Comment, number>({
        query: id => ({
          url: `comment/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'Comment' as const, arg }, 'Comments']
            : ['Comments'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetCommentsQuery,
  usePatchCommentMutation,
  useGetCommentQuery,
  useCreateCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
  usePrefetch: useCommentsPrefetch,
} = comment;
